import { useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, TextDivider, Input, Card } from '../components/primitives/';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../assets/pplogo.png';
import { useTranslation } from '../hooks/useTranslation';
import AuthButtons from '../components/AuthButtons';
import useTimedError from '../hooks/useTimedError';
import { catchErrorAuth } from '../ErrorUtility';
//import { CheckMark, XMark } from '../components/svg-rfc';
import serviceIcons from '../assets/service-icons';

export default function Signup() {
    const t = useTranslation();
    const location = useLocation();
    
    const emailRef = useRef();
    const passwordRef = useRef();
    // const passwordConfirmRef = useRef();
    const [email, setEmail] = useState(location.state?.email || "")
    const [pass, setPass] = useState("")
    //const [passConfirm, setPassConfirm] = useState("")
    const { signup, sendVerificationCode } = useAuth();
    const [error, setError] = useTimedError();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [passwordComplexity, setPasswordComplexity] = useState([
        {
            name: "length",
            value: false,
            message: t("At Least 8 Characters"),
            error: t("Password must be at least 8 characters long")
        },
        {
            name: "uppercase",
            value: false,
            message: t("At Least One Uppercase Letter"),
            error: t("Password must contain at least one uppercase letter")
        },
        {
            name: "lowercase",
            value: false,
            message: t("At Least One Lowercase Letter"),
            error: t("Password must contain at least one lowercase letter")
        },
        {
            name: "number",
            value: false,
            message: t("At Least One Number"),
            error: t("Password must contain at least one number")
        },
        // {
        //     name: "matching",
        //     value: false,
        //     message: t("Matching Passwords"),
        //     error: t("Passwords do not match")
        // }
    ]);
    const [passwordComplexityVisible, setPasswordComplexityVisible] = useState(false);

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handlePassChange = (event) => {
        setPass(event.target.value);
        setPasswordComplexity(passwordComplexity.map((complexity) => ({
            ...complexity,
            value: (() => {
                switch (complexity.name) {
                    case "length":
                        return event.target.value.length >= 8;
                    case "uppercase":
                        return /[A-Z]/.test(event.target.value);
                    case "lowercase":
                        return /[a-z]/.test(event.target.value);
                    case "number":
                        return /\d/.test(event.target.value);
                    // case "matching":
                    //     return event.target.value && (event.target.value === passwordConfirmRef.current.value);
                    default:
                        return false;
                }
            })()
        })));
    };

    // const handlePassConfirmChange = (event) => {
    //     setPassConfirm(event.target.value);
    //     setPasswordComplexity(passwordComplexity.map((complexity) => ({
    //         ...complexity,
    //         value: complexity.name === "matching" ? event.target.value && (event.target.value === passwordRef.current.value) : complexity.value
    //     })));
    // };

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            passwordComplexity.forEach((complexity) => {
                if (!complexity.value) {
                    throw new Error(complexity.error);
                }
            });
        } catch (error) {
            setError(error.message);
            return;
        }

        const [result, errorCode, errorMessage] = await catchErrorAuth(signup, emailRef.current.value, passwordRef.current.value);
        if (errorCode) {
            setError(errorMessage);
            setLoading(false);
        } else {
            navigate("/verify");
            sendVerificationCode();
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-outer-container">
                <div className="logo-group">
                    <a href="https://pageparrot.com" className="logo-group-inner">
                        <img className="logo-signin" src={Logo} alt="Page Parrot Logo" />
                        <h2 className="logo-heading w-900 text-center">{t('Page Parrot')}</h2>
                    </a>
                </div>
                <Card ctype="auth-container centered">
                    <h2 className="text-center" style={{ fontSize: "20px", marginTop: "0" }}>{t('Create Your Account')}</h2>
                    <AuthButtons setLoading={setLoading} setError={setError} signup={true} />
                    <div styles={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <TextDivider>{t('Or')}</TextDivider>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <Input ref={emailRef} value={email} labelText={t('Email')} id="email" required={true} onChange={handleEmailChange} />
                        <Input ref={passwordRef} value={pass} labelText={t('Password')} id="password" type="password" required={true} onChange={handlePassChange} onFocus={() => setPasswordComplexityVisible(true)} />
                        {/* <Input ref={passwordConfirmRef} value={passConfirm} labelText={t('Confirm Password')} id="password-confirm" type="password" required={true} onChange={handlePassConfirmChange} onFocus={() => setPasswordComplexityVisible(true)} /> */}
                        {passwordComplexityVisible && <div className="password-complexity mv-15">
                            {passwordComplexity.map((complexity) => (
                                <div key={complexity.name} className={`password-complexity-item ${complexity.value ? "valid" : "invalid"} entering`}>
                                    {complexity.value ? <img src={serviceIcons["CheckMark.svg"]} alt="Check Mark" className="password-complexity-icon valid" /> : <img src={serviceIcons["XMark.svg"]} alt="X Mark" className="password-complexity-icon invalid" />}
                                    <span className="password-complexity-message">{complexity.message}</span>
                                </div>
                            ))}
                        </div>}
                        <Button disabled={loading} buttonType = "submit" loading={loading} error={error}>
                            {error ? error : t('Get Started')}
                        </Button>
                    </form>
                    <div className="auth-info">
                        <p>{t('By creating an account, you agree to Page Parrot\'s')} 
                            <a href="https://pageparrot.com/terms-of-service" target="_blank" rel="noopener noreferrer">{` ${t('Terms of Service')} `}</a>
                            {` ${t('and')} `}
                            <a href="https://pageparrot.com/privacy-policy" target="_blank" rel="noopener noreferrer">{` ${t('Privacy Policy')}`}</a>.
                        </p>
                    </div>
                    <p className="w-700 text-center" style={{ margin: "0" }}>{t('Already have an account?')}</p>
                    <div className="text-center signup-link link-text">
                        <Link to="/login" state={{ email: emailRef.current?.value || email }} className="w-700">
                            {t('Sign In Here')}
                        </Link>
                    </div>
                </Card>             
            </div>
        </div>
    )
}